import { FC, useMemo } from 'react';
import type { TileData } from 'state/quotes/dashboard/model';

import { SkeletonTile } from '../skeleton/Tile.skeleton';
import type { TermsheetApi } from './components/termsheet/termsheetContext';
import { TermsheetContext } from './components/termsheet/termsheetContext';
import { WithClosingTile, WithRefreshingTile } from './reminders';
import { RawTile } from './Tile';
import type { TileOwnProps } from './Tile.connect';
import { useDispatchToProps, useMapStateToProps } from './Tile.connect';

export const Tile: FC<TileOwnProps> = ({ quoteId, customGroups, className, onDashboardClosed, onTileClosed }) => {
  const { data, metadata, regulationData, canBeRepriced, hasAssociatedTab, termsheetParams, formData } =
    useMapStateToProps({
      quoteId,
    });
  const {
    onWorkspaceTabOpened,
    onReprice,
    onPinToggled,
    onGroupChanged,
    onUnGrouped,
    onTermsheetSuccess,
    onTermsheetError,
    onNotificationShown,
  } = useDispatchToProps({ quoteId, onDashboardClosed }, { data, metadata });

  const termsheetApi = useMemo<TermsheetApi | undefined>(
    () =>
      termsheetParams
        ? {
            locales: termsheetParams.locales,
            quoteQuery: termsheetParams.quoteQuery,
            onSuccess(locale) {
              onTermsheetSuccess(termsheetParams.tabId, locale);
            },
            onError(locale, error) {
              onTermsheetError(termsheetParams.tabId, locale, error);
              onNotificationShown(
                'danger',
                { id: 'dashboard.notifictation.termsheet.error.title' },
                { id: 'downloadTermsheet.functional.error' },
              );
            },
          }
        : undefined,
    [onNotificationShown, onTermsheetError, onTermsheetSuccess, termsheetParams],
  );

  if (data === undefined) {
    return <SkeletonTile onClosed={onTileClosed} />;
  }
  const tileData: TileData = {
    ...data,
    remunerationMode: formData?.RemunerationMode,
    fixedAmountInPercent: formData?.FixedAmountInPercent,
  };
  return (
    <TermsheetContext.Provider value={termsheetApi}>
      <WithClosingTile onClose={onTileClosed}>
        {({ onClosing }) => (
          <WithRefreshingTile onRefresh={canBeRepriced ? onReprice : undefined}>
            {({ onRefreshing }) => (
              <RawTile
                hasAssociatedTab={hasAssociatedTab}
                quoteId={quoteId}
                className={className}
                customGroups={customGroups}
                data={tileData}
                metadata={metadata}
                onWorkspaceTabOpened={onWorkspaceTabOpened}
                onClosed={onClosing}
                onPinToggled={onPinToggled}
                onReprice={onRefreshing}
                regulationData={regulationData}
                onGroupChanged={onGroupChanged}
                onUnGrouped={onUnGrouped}
              />
            )}
          </WithRefreshingTile>
        )}
      </WithClosingTile>
    </TermsheetContext.Provider>
  );
};

Tile.displayName = 'TileConnected';
